<template>
  <main
    class="page"
    role="main"
    aria-labelledby="tickets-h1"
  >
    <div class="page-header">
      <h1
        id="tickets-h1"
        class="h1"
      >
        Tickets
      </h1>
      <button
        v-if="!isTicketmaster"
        class="btn btn-primary"
        @click="openCreateDialog"
      >
        Neu
      </button>
    </div>

    <!-- Dialoge -->
    <TicketCreateDialog
      :open="createOpen"
      @close="closeCreateDialog"
      @created="onCreated"
    />
    <TicketEditDialog
      :open="editOpen"
      :ticket="editTicket"
      @close="closeEdit"
      @saved="onEdited"
    />
    <TicketViewDialog
      :open="viewOpen"
      :ticket-id="viewTicketId"
      @close="closeView"
    />

    <div class="card">
      <div class="filters">
        <input
          v-model="q"
          placeholder="Betreff oder letzte Nachricht suchen…"
          autocomplete="off"
        >
        <select v-model="status">
          <option value="">
            Alle
          </option>
          <option value="open">
            Offen
          </option>
          <option value="in_progress">
            In Bearbeitung
          </option>
          <option value="resolved">
            Gelöst
          </option>
          <option value="closed">
            Geschlossen
          </option>
        </select>
        <button
          class="btn btn-primary"
          @click="resetFilters"
        >
          Reset
        </button>
      </div>

      <div :class="['table-header', isAdmin ? 'has-author' : 'has-author']">
        <div>#</div>
        <div>Betreff</div>
        <div>Letzte Nachricht</div>
        <div>
          Urheber
        </div>
        <div>Status</div>
        <div>Aktualisiert</div>
        <div class="text-right">
          Aktionen
        </div>
      </div>

      <div
        v-for="t in paged"
        :key="t.id"
        :class="['table-row', isAdmin ? 'has-author' : 'has-author', { unread: hasUnread(t.id) }]"
      >
        <div><strong>#{{ t.id }}</strong></div>
        <div class="ellipsis subject">
          {{ t.subject }}
        </div>
        <div class="muted ellipsis">
          {{ t.lastMessagePreview || '—' }}
        </div>
        <div
          class="ellipsis"
        >
          {{ t.creatorUserName ?? t.creator_user_name ?? '—' }}
          <span
            v-if="t.isPrivate || t.is_private"
            class="badge"
            style="margin-left:6px;"
          >privat</span>
        </div>
        <div>
          <span
            class="badge"
            :class="badgeClass(t.status)"
          >{{ statusLabel(t.status) }}</span>
        </div>
        <div class="muted">
          {{ formatDate(t.updatedAt || t.updated_at) }}
        </div>
        <div
          class="text-right"
          style="display:inline-flex; gap:8px; justify-content:flex-end;"
        >
          <button
            class="icon-btn"
            :title="'Ansehen'"
            @click="openView(t.id)"
          >
            <i
              class="pi pi-eye"
              aria-hidden="true"
              style="font-size:18px;color:var(--muted);"
            />
          </button>
        </div>
      </div>

      <div
        v-if="loading"
        class="table-row skeleton"
      >
        <div class="sk-line" />
        <div class="sk-line" />
        <div class="sk-badge" />
        <div class="sk-line short" />
        <div class="sk-line short" />
        <div class="sk-btn" />
      </div>

      <p
        v-if="!loading && filtered.length === 0"
        class="muted"
      >
        Keine Tickets.
      </p>
      <p
        v-if="listError"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ listError }}
      </p>

      <div class="mt-12 pager">
        <span
          class="muted"
          style="margin-right:auto;"
        >Gesamt: {{ filtered.length }}</span>
        <label
          for="limit"
          class="muted"
        >pro Seite:</label>
        <select
          id="limit"
          :value="limit"
          @change="onLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prev"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="next"
        >
          Weiter
        </button>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useTicketsStore } from '@/stores/tickets';
import { useAuthStore } from '@/stores/auth';
import TicketCreateDialog from '@/components/tickets/TicketCreateDialog.vue';
import TicketEditDialog from '@/components/tickets/TicketEditDialog.vue';
import TicketViewDialog from '@/components/tickets/TicketViewDialog.vue';

const auth = useAuthStore();
const roleId = computed(() => auth.user?.roleId ?? auth.user?.role_id ?? null);
const isAdmin = computed(() => roleId.value === 1);
const currentUserId = computed(() => auth.user?.id ?? auth.user?.userId ?? auth.user?.user_id ?? null);
// Ticketmaster aktuell: user_id = 2
const isTicketmaster = computed(() => Number(currentUserId.value) === 2);

const store = useTicketsStore();

const q = ref('');
const status = ref('');
const page = ref(1);
const limit = ref(20);

const createOpen = ref(false);
const editOpen = ref(false);
const editTicket = ref(null);
const viewOpen = ref(false);
const viewTicketId = ref(null);

const loading = computed(() => store.loading);
const listError = computed(() => store.error);
const items = computed(() => store.list || []);

const filtered = computed(() => {
  let base = items.value;

  // Private Tickets nur für Ersteller (creatorUserId) und Ticketmaster (id=2) sichtbar machen
  const myId = Number(currentUserId.value);
  const isTm = isTicketmaster.value === true;
  base = base.filter(t => {
    const priv = Boolean(t.isPrivate ?? t.is_private ?? false);
    const creatorId = Number(t.creatorUserId ?? t.creator_user_id ?? NaN);
    if (!priv) return true;
    if (isTm) return true;
    return Number.isFinite(myId) && Number.isFinite(creatorId) && myId === creatorId;
  });

  if (status.value) base = base.filter(t => t.status === status.value);
  const n = q.value.trim().toLowerCase();
  if (!n) return base;
  return base.filter(t => {
    const subject = (t.subject || '').toLowerCase();
    // robuste Ableitung des letzten Nachrichten-Texts aus möglichen Feldern
    const lastMsg = (
      t.lastMessagePreview ||
      t.last_message_preview ||
      t.lastMessage ||
      t.last_message ||
      t.lastMessageText ||
      t.last_message_text ||
      ''
    ).toLowerCase();
    return subject.includes(n) || lastMsg.includes(n) || String(t.id).includes(n);
  });
});

const unreadMap = computed(() => store.unreadMap || {});
function hasUnread(id) {
  const key = Number(id);
  if (!Number.isFinite(key)) return false;
  const cnt = Number(unreadMap.value[key] ?? 0);
  return Number.isFinite(cnt) && cnt > 0;
}

const pages = computed(() => Math.max(1, Math.ceil(filtered.value.length / limit.value)));
const paged = computed(() => {
  const start = (page.value - 1) * limit.value;
  return filtered.value.slice(start, start + limit.value);
});

function onLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
}
function next() { if (page.value < pages.value) page.value++; }
function prev() { if (page.value > 1) page.value--; }
function resetFilters() {
  q.value = '';
  status.value = '';
  page.value = 1;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(d);
}

function badgeClass(s) {
  return {
    'badge-info': s === 'in_progress',
    'badge-success': s === 'resolved',
    'badge-danger': s === 'closed',
    'badge-warn': s === 'open'
  };
}
function statusLabel(s) {
  if (s === 'open') return 'Offen';
  if (s === 'in_progress') return 'In Bearbeitung';
  if (s === 'resolved') return 'Gelöst';
  if (s === 'closed') return 'Geschlossen';
  return s || '—';
}

function openView(id) {
  viewTicketId.value = Number(id);
  viewOpen.value = true;
}
function closeView() {
  viewOpen.value = false;
  viewTicketId.value = null;
}

function openCreateDialog() {
  if (isTicketmaster.value) {
    return;
  }
  createOpen.value = true;
}
function closeCreateDialog() {
  createOpen.value = false;
}
async function onCreated() {
  closeCreateDialog();
  await store.fetchList();
  // Keine automatische Navigation/URL-Aufruf nach Erstellung
}

function closeEdit() {
  editOpen.value = false;
  editTicket.value = null;
}
async function onEdited() {
  closeEdit();
  await store.fetchList();
}

onMounted(async () => {
  try {
    await store.fetchList();
  } catch { /* ignore */ }
  try {
    await Promise.allSettled([store.fetchUnreadCount(), store.fetchUnreadMap()]);
  } catch { /* ignore */ }
});
</script>

<style scoped>
.page { padding: 16px; background: var(--bg); color: var(--text); }
.page-header { display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--border); padding-bottom:8px; margin-bottom:12px; }
.card { background: var(--surface); border:1px solid var(--border); border-radius:10px; padding:16px; }

.form { display:flex; flex-direction:column; gap:12px; }
.field input, .field textarea, .filters input, .filters select {
  height: 38px; padding: 8px; border: 1px solid var(--border); border-radius: 8px; background: var(--surface);
}
.field textarea { height: auto; }

.filters { display:flex; gap:8px; align-items:end; margin-bottom:12px; }

.table-header, .table-row {
  display: grid;
  gap: 8px;
  align-items: center;
  font-size: 14px;
}

/* Mit Urheber-Spalte (Admins) → 7 Spalten */
.table-header.has-author, .table-row.has-author {
  grid-template-columns: 80px 1.2fr 2fr 200px 160px 80px 80px
}

/* Ohne Urheber-Spalte → 6 Spalten */
.table-header.no-author, .table-row.no-author {
  grid-template-columns: 80px 1.2fr 2fr 160px 80px 80px
}
.table-header { font-size:12px; color:var(--muted); padding:6px 0; border-bottom:1px solid var(--border); }
.table-row { padding:5px 0; border-bottom:1px solid var(--border); }
.ellipsis { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.table-row.unread { font-weight: 600; color: var(--text); }

.icon-btn {
  display:inline-flex; align-items:center; justify-content:center; height:36px; width:36px;
  border-radius:8px; border:1px solid var(--border); background:var(--surface); color:var(--text); cursor:pointer;
}
.icon-btn:hover { background: color-mix(in srgb, var(--primary) 8%, transparent); }

.badge {
  display:inline-block; font-size:12px; line-height:16px; padding:2px 8px; border-radius:999px; border:1px solid var(--border); color:var(--muted);
}
.badge-info { color: var(--info); border-color: rgba(37,99,235,.5); background: rgba(37,99,235,.08); }
.badge-success { color: var(--success); border-color: rgba(16,185,129,.5); background: rgba(16,185,129,.08); }
.badge-danger { color: var(--danger); border-color: rgba(239,68,68,.5); background: rgba(239,68,68,.08); }
.badge-warn { color: var(--warn); border-color: rgba(245,158,11,.5); background: rgba(245,158,11,.08); }

.spinner { width:16px; height:16px; border:2px solid color-mix(in srgb, var(--primary) 30%, transparent); border-top-color:var(--primary); border-radius:50%; margin-right:6px; animation: spin .9s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.skeleton .sk-line { height: 12px; background: color-mix(in srgb, var(--bg) 50%, white); border-radius:6px; }
.skeleton .sk-line.short { width:70%; }
.skeleton .sk-badge { width: 64px; height: 18px; border-radius: 999px; background: color-mix(in srgb, var(--bg) 50%, white); }
.skeleton .sk-btn { width: 120px; height: 32px; border-radius: 8px; background: color-mix(in srgb, var(--bg) 50%, white); }

.pager { display:flex; align-items:center; gap:8px; justify-content:flex-end; }

@media (max-width: 768px) {
  .table-header, .table-row { grid-template-columns: 60px 1fr 120px 1fr 120px 80px; }
  .filters { flex-direction: column; align-items: stretch; }
}
</style>
